'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import { Popover } from '@headlessui/react';
import {
  EmailShareButton,
  FacebookShareButton,
  LinkedinShareButton,
  TwitterShareButton,
  WhatsappIcon,
  WhatsappShareButton,
} from 'react-share';
import type { MentorshipListProgramView } from '@/features/mentorship';
import { buildMentorshipShareUrl } from '@/features/mentorship';

type Props = {
  program: MentorshipListProgramView;
};

export default function ProgramCard({ program }: Props) {
  const [shareUrl, setShareUrl] = useState(() =>
    buildMentorshipShareUrl(program.id)
  );
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setShareUrl(buildMentorshipShareUrl(program.id));
  }, [program.id]);

  const handleCopy = () => {
    if (typeof window !== 'undefined' && navigator.clipboard && shareUrl) {
      void navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <article className="flex h-full min-h-0 flex-col w-[min(280px,85vw)] sm:w-full shrink-0 snap-center rounded-xl overflow-hidden border border-gray-100 bg-white shadow-sm">
      <div className="relative aspect-[16/10] w-full shrink-0 bg-gray-100">
        <Image
          src={program.cardImage}
          alt={program.cardImageAlt}
          fill
          className="object-cover rounded-b-xl"
          sizes="(max-width: 640px) 85vw, 280px"
        />
      </div>
      <div className="flex min-h-0 flex-1 flex-col p-3">
        <p className="text-md text-[#535862] leading-snug shrink-0">
          {program.dateLabel}
        </p>
        <p className="mt-2 text-lg text-[#535862] leading-snug font-medium line-clamp-1 shrink-0">
          {program.listTitle}
        </p>
        <div
          className="mt-2 min-h-[3.25rem] text-md text-[#414651] leading-relaxed line-clamp-2 break-words [&_p]:mb-1 [&_p:last-child]:mb-0 [&_ul]:my-1 [&_ul]:list-disc [&_ul]:pl-4 [&_ol]:my-1 [&_ol]:list-decimal [&_ol]:pl-4 [&_li]:mb-0.5 [&_a]:text-[#0AC1CE] [&_a]:underline"
          dangerouslySetInnerHTML={{ __html: program.shortDescription }}
        />
        <div className="mt-auto flex items-stretch gap-2 pt-2">
          <Link
            href={`/mentorship/${program.id}`}
            className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg text-white text-sm font-medium py-2.5 px-3"
            style={{
              background: 'linear-gradient(90deg, #004DA0 0%, #004DA0 100%)',
              boxShadow:
                '0px 2px 4px -2px #0A0D120F, 0px 4px 8px -2px #0A0D121A',
            }}
          >
            <Image
              src="/mentor/mentor-star.png"
              alt=""
              width={16}
              height={16}
            />
            Дэлгэрэнгүй
          </Link>
          {shareUrl ? (
            <Popover className="relative shrink-0">
              <Popover.Button
                type="button"
                onClick={(e) => e.stopPropagation()}
                className="w-9 h-9 flex items-center justify-center shrink-0 rounded-lg border-0 p-0 cursor-pointer"
                style={{ background: '#FFA726' }}
                aria-label="Хуваалцах"
              >
                <Image
                  src="/mentor/mentor-share.svg"
                  alt=""
                  width={16}
                  height={16}
                />
              </Popover.Button>
              <Popover.Panel
                className="absolute z-50 bottom-full mb-2 right-0"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-4 bg-white rounded-xl shadow-lg flex flex-wrap gap-3 w-[260px] relative border border-gray-200">
                  <FacebookShareButton url={shareUrl}>
                    <Image
                      src="/images/icons/facebook.svg"
                      alt="Facebook"
                      width={32}
                      height={32}
                    />
                  </FacebookShareButton>
                  <LinkedinShareButton url={shareUrl}>
                    <Image
                      src="/images/icons/linkedin.svg"
                      alt="LinkedIn"
                      width={32}
                      height={32}
                    />
                  </LinkedinShareButton>
                  <TwitterShareButton url={shareUrl} title={program.listTitle}>
                    <Image
                      src="/images/icons/twitter.svg"
                      alt="Twitter"
                      width={32}
                      height={32}
                    />
                  </TwitterShareButton>
                  <WhatsappShareButton
                    url={shareUrl}
                    title={program.listTitle}
                    separator=" — "
                  >
                    <WhatsappIcon size={32} round />
                  </WhatsappShareButton>
                  <EmailShareButton
                    url={shareUrl}
                    subject={program.listTitle}
                    body={`${program.listTitle}: ${shareUrl}`}
                  >
                    <Image
                      src="/images/icons/email.svg"
                      alt="Email"
                      width={32}
                      height={32}
                    />
                  </EmailShareButton>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCopy();
                    }}
                    className="cursor-pointer"
                    aria-label="Холбоос хуулах"
                  >
                    <Image
                      src="/images/icons/link.svg"
                      alt="Хуулж авах"
                      width={32}
                      height={32}
                    />
                  </button>
                  {copied ? (
                    <div className="absolute top-full mt-1 left-1/2 -translate-x-1/2 bg-gray-800 text-white text-sm px-3 py-1 rounded shadow z-20 whitespace-nowrap">
                      Амжилттай хууллаа
                    </div>
                  ) : null}
                </div>
              </Popover.Panel>
            </Popover>
          ) : (
            <div
              className="flex w-9 h-9 shrink-0 items-center justify-center rounded-lg opacity-50 bg-[#FFA726]"
              aria-hidden
            >
              <Image
                src="/mentor/mentor-share.svg"
                alt=""
                width={16}
                height={16}
              />
            </div>
          )}
        </div>
      </div>
    </article>
  );
}
